// The unified inbox (WhatsApp + email): one cursor-paged thread list, the "waiting for a reply"
// counts and the done toggle (API modules/inbox). Opening, replying to or making a deal from a
// thread still goes through the per-channel endpoints (whatsapp-api.ts, outreach-api.ts), picked
// by the thread key's prefix.
//
// These routes answer with a bare body (the page `{ data, nextCursor }` IS the body, not
// apiClient's `{ data }` envelope), so this uses the raw axios client and returns the body as is.
import { api } from './api'

export type InboxChannel = 'all' | 'whatsapp' | 'email'
export type InboxFilter = 'all' | 'needs_reply' | 'unmatched'
/** Email enrollment statuses the API filters on (`opened` = opened at least once). */
export type InboxEmailStatus =
  | 'sent'
  | 'opened'
  | 'replied'
  | 'bounced'
  | 'unsubscribed'
  | 'send_failed'
  | 'suppressed'
  | 'completed'

/**
 * One thread of the unified inbox. `key` names the per-channel detail endpoint:
 * `wa:<conversationId>`, `em:<enrollmentId>` or `emu:<emailMessageId>` (unmatched email, read-only).
 */
export interface InboxThreadRow {
  key: string
  channel: 'whatsapp' | 'email'
  contactId: string | null
  /** Null for an unmatched thread, or a WhatsApp thread whose contact is gone. */
  contact: { name: string } | null
  company: { id: string; name: string } | null
  /** phoneE164 for WhatsApp, the email address for email. */
  address: string
  snippet: string
  lastMessageAt: string | null
  lastDirection: 'inbound' | 'outbound' | null
  /** Last real message inbound and not marked done after it: what the counts count. */
  needsReply: boolean
  /** WhatsApp unread messages; always 0 for email. */
  unreadCount: number
  /** WhatsApp 24h customer-service window; null for email (and a WhatsApp thread never opened). */
  windowExpiresAt: string | null
  dealId: string | null
  /** Email enrollment status; null for WhatsApp and unmatched email. */
  status: string | null
  /** Sent from a one-off ("direct") email, not a campaign. */
  isDirect: boolean
  unmatched: boolean
  doneAt: string | null
  /** Email campaign; always null for WhatsApp. */
  campaign: { id: string; name: string } | null
  /** WhatsApp sender number id; null for email. */
  senderId: string | null
}

export interface InboxThreadsPage {
  data: InboxThreadRow[]
  /** Pass back as `cursor` for the next page; null on the last page. */
  nextCursor: string | null
}

/** Threads waiting for a reply per channel (matched threads only), and the unmatched thread count
 *  (always 0 for non-admins). */
export interface InboxCounts {
  all: number
  whatsapp: number
  email: number
  unmatched: number
}

export interface InboxDoneResult {
  key: string
  doneAt: string | null
}

export interface InboxThreadsParams {
  channel?: InboxChannel
  filter?: InboxFilter
  /** Implies email only. */
  status?: InboxEmailStatus
  contactId?: string
  /** Narrows WhatsApp rows only. */
  senderId?: string
  cursor?: string
  /** 1..100, default 30. */
  limit?: number
}

export interface InboxCountsParams {
  /** Narrows the WhatsApp counts only. */
  senderId?: string
}

const doneUrl = (key: string) => `/inbox/threads/${encodeURIComponent(key)}/done`

export const inboxApi = {
  listThreads: (params: InboxThreadsParams = {}): Promise<InboxThreadsPage> =>
    api.get<InboxThreadsPage>('/inbox/threads', { params }).then((res) => res.data),
  counts: (params: InboxCountsParams = {}): Promise<InboxCounts> =>
    api.get<InboxCounts>('/inbox/counts', { params }).then((res) => res.data),
  markDone: (key: string): Promise<InboxDoneResult> =>
    api.post<InboxDoneResult>(doneUrl(key)).then((res) => res.data),
  clearDone: (key: string): Promise<InboxDoneResult> =>
    api.delete<InboxDoneResult>(doneUrl(key)).then((res) => res.data),
}
